import { Spinner } from "@fluentui/react"
import {
  dateAndTime,
  EnvelopeKind,
  EnvelopeRecord,
  errorToString,
  ENVELOPE_SECTIONS,
  StatusData,
} from "@oneethos/shared"
import { useState } from "react"
import { FaExternalLinkAlt } from "react-icons/fa"
import { toast } from "react-toastify"
import api from "../api-client"
import { useAppState } from "../hooks"
import { Link } from "./link"

const envelopeStatusLabel = (status: EnvelopeRecord['status']): string => {
  switch (status) {
    case 'voided':
      return 'Voided'
    case 'customer-signed':
      return 'Signed'
    case 'draft':
      return 'Draft'
    case 'sent':
      return 'Sent'
    default:
      return 'Unknown'
  }
}

type EnvelopeLinkProps = {
  envelope: EnvelopeRecord
  appUrl?: string
}

const EnvelopeLink = ({ envelope, appUrl }: EnvelopeLinkProps) => {
  const { status } = envelope
  const label = `${envelopeStatusLabel(status)} - ${dateAndTime(new Date(envelope.timestamp))}`

  return (
    <div className="mb-1">
      <Link
        href={`${appUrl}/send/documents/details/${envelope.envelopeId}`}
        target="_blank"
        variant={status === 'voided' ? 'secondary' : 'primary'}
      >
        {label}
        <FaExternalLinkAlt className="ms-2" />
      </Link>
    </div>
  )
}

type DraftEnvelopeButtonProps = {
  label: string
  kind: EnvelopeKind
  onDraft: (kind: EnvelopeKind) => void
}

const DraftEnvelopeButton = ({
  label,
  kind,
  onDraft,
}: DraftEnvelopeButtonProps) => {
  return (
    <button
      type="button"
      className={`btn btn-primary btn-sm mt-2`}
      onClick={(ev) => {
        ev.preventDefault()
        onDraft(kind)
      }}
    >
      {label}
    </button>
  )
}

type EnvelopeSectionProps = {
  label: string
  envelopes: EnvelopeRecord[]
  appUrl?: string
  draftingKind: EnvelopeKind | null
  onDraft: (kind: EnvelopeKind) => void
  kind: EnvelopeKind
}

const EnvelopeSection = ({
  label,
  envelopes,
  appUrl,
  draftingKind,
  onDraft,
  kind,
}: EnvelopeSectionProps) => {
  const isCurrentSectionDrafting = draftingKind === kind

  return (
    <div className="mb-3">
      <div className="text-uppercase text-secondary fw-bold small mb-2">{label}</div>
      {envelopes.map((envelope) => (
        <EnvelopeLink
          key={envelope.envelopeId}
          envelope={envelope}
          appUrl={appUrl}
        />
      ))}
      <div className="mt-2">
        {isCurrentSectionDrafting ? (
          <Spinner />
        ) : (
          <DraftEnvelopeButton
            label={`Draft New ${label}`}
            kind={kind}
            onDraft={onDraft}
          />
        )}
      </div>
    </div>
  )
}

type DocusignEnvelopesProps = {
  project: StatusData
  onChange: (project: StatusData) => void
}

export const getVisibleEnvelopeSections = (
  envelopes: EnvelopeRecord[],
  applicationReceived?: string
) => {
  return ENVELOPE_SECTIONS.map((section) => ({
    ...section,
    envelopes: envelopes.filter((envelope) => envelope.kind === section.kind),
  })).filter((section) => (
    section.envelopes.length > 0 ||
    (section.kind === EnvelopeKind.LoanDocs && Boolean(applicationReceived))
  ))
}

export const DocusignEnvelopes = ({ project, onChange }: DocusignEnvelopesProps) => {
  const { tenant } = useAppState()
  const [draftingKind, setDraftingKind] = useState<EnvelopeKind | null>(null)
  const envelopes = project.envelopes || []
  const appUrl = tenant.config.docusign?.appUrl

  const sections = getVisibleEnvelopeSections(
    envelopes,
    project.applicationReceived
  )

  const draftEnvelope = (kind: EnvelopeKind) => {
    const kindLabel = ENVELOPE_SECTIONS.find((s) => s.kind === kind)?.label ?? kind
    const dt = project.updatedDate?.toLocaleString()
    if (!window.confirm(
      `To prevent errors, please make sure this project has synced the latest changes from Airtable before proceeding. This record was last updated at ${dt}.\n\nContinue to draft ${kindLabel}?`
    )) {
      return
    }

    setDraftingKind(kind)
    api.post(`/loanapps/${project.id}/envelopes`, { kind })
      .then((res) => {
        toast.success(`Draft ${kindLabel} Created!`)
        onChange(new StatusData(res))
      })
      .catch((ex) => { toast.error(errorToString(ex)) })
      .finally(() => setDraftingKind(null))
  }

  return (
    <div className="docusign-envelopes">
      <h6>Docusign Envelopes</h6>

      {sections.length === 0 ? (
        <div className="alert alert-secondary text-small py-2">No envelopes found</div>
      ) : (
        sections.map((section) => (
          <EnvelopeSection
            key={section.kind}
            label={section.label}
            envelopes={section.envelopes}
            appUrl={appUrl}
            draftingKind={draftingKind}
            onDraft={draftEnvelope}
            kind={section.kind}
          />
        ))
      )}
    </div>
  )
}
